import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import styles from './index.module.less';
import { Button } from 'antd';
import classNames from 'classnames';
import Image from 'next/image';
import { SectionData } from '@/types/home';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'next-i18next';

export default function MainBenefitsContent({ data, onTrackClick }: { data?: SectionData; onTrackClick?: () => void }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleIconClick = (index: number) => {
    if (!isScrolling) {
      scrollRef.current?.scrollTo({
        left: index * scrollRef.current.offsetWidth,
      });
    }
  };
  const { t } = useTranslation();
  const handleArrowClick = (direction: 'left' | 'right') => {
    // const currentIndex = icons.indexOf(selectedIcon);
    // const newIndex = direction === 'left'
    //   ? (selectedIndex - 1 + data?.cards?.length) % data?.cards?.length
    //   : (selectedIndex + 1) % data?.cards?.length;

    const newIndex =
      direction === 'left'
        ? selectedIndex - 1 > 0
          ? selectedIndex - 1
          : 0
        : selectedIndex + 1 < data?.cards?.length
          ? selectedIndex + 1
          : data?.cards?.length - 1;

    if (newIndex !== selectedIndex) {
      setIsScrolling(true);

      setSelectedIndex(newIndex);
      scrollRef.current?.scrollTo({
        left: newIndex * scrollRef.current.offsetWidth,
        // behavior: 'smooth'
      });

      setTimeout(() => setIsScrolling(false), 250);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: selectedIndex * scrollRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current && !isScrolling) {
        const scrollPosition = scrollRef.current.scrollLeft;
        const screenWidth = scrollRef.current.offsetWidth;
        const currentIndex = Math.round(scrollPosition / screenWidth);
        // setSelectedIcon(icons[currentIndex]);
        setSelectedIndex(currentIndex);
      }
    };

    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isScrolling]);

  if (!data?.cards?.length) {
    return null;
  }

  return data ? (
    <div className={styles.instagramTracker}>
      {data?.title && <h2 className={styles.instagramTracker__title}>{data?.title}</h2>}

      {data?.desc && <p className={styles.instagramTracker__description}>{data?.desc}</p>}

      <div className={styles.instagramTracker__card}>
        <div className={styles.instagramTracker__content}>
          <div className={styles.instagramTracker__icons}>
            <ChevronLeft
              className={`${styles.instagramTracker__arrow} ${styles.instagramTracker__arrow_left}`}
              onClick={() => handleArrowClick('left')}
            />
            <div className={styles.instagramTracker__iconList}>
              {data?.cards?.map((item, index) => {
                return (
                  <div
                    key={item?.title}
                    className={`${styles.instagramTracker__icon} ${
                      selectedIndex === index ? styles.instagramTracker__icon_selected : ''
                    }`}
                    onClick={() => handleIconClick(index)}
                  >
                    <SVG
                      // src={item.svg}
                      src={`/n-api/image-proxy?url=${encodeURIComponent(item?.svg)}`}
                      width={'30px'}
                      height={'30px'}
                      title="svg"
                      style={{ color: selectedIndex === index ? '#FFF' : '#666' }}
                      className={styles.image}
                    />
                  </div>
                );
              })}
            </div>
            <ChevronRight
              className={`${styles.instagramTracker__arrow} ${styles.instagramTracker__arrow_right}`}
              onClick={() => handleArrowClick('right')}
            />
          </div>

          <div className={styles.instagramTracker__contentSlider}>
            <div ref={scrollRef} className={styles.instagramTracker__contentWrapper}>
              {data?.cards?.map((item, index) => (
                <div key={item?.title} className={styles.instagramTracker__contentItem}>
                  <div className={styles.instagramTracker__contentInner}>
                    <h3
                      className={classNames([
                        styles.instagramTracker__contentTitle,
                        selectedIndex === index && styles.activeColor,
                      ])}
                    >
                      {item?.title}
                    </h3>
                    <p className={styles.instagramTracker__contentDescription}>{item?.desc}</p>
                    <div className={styles.instagramTracker__imgContainer}>
                      <Image
                        src={data?.cards[selectedIndex].imgPath}
                        alt={''}
                        width={100}
                        height={100}
                        layout="responsive"
                        objectFit="cover"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.img}>
          <Image
            src={data?.cards[selectedIndex].imgPath}
            alt={''}
            width={100}
            height={100}
            layout="responsive"
            objectFit="cover"
          />
        </div>

        <div className={styles.contentList}>
          {data?.cards?.map((item, index) => (
            <div
              className={classNames([styles.contentListItem, selectedIndex === index && styles.contentListItemActive])}
              key={index}
              onClick={() => {
                setSelectedIndex(index);
              }}
            >
              <div className={styles.contentItemTitle}>
                <SVG
                  // src={item.svg}
                  src={`/n-api/image-proxy?url=${encodeURIComponent(item?.svg)}`}
                  width={'24px'}
                  height={'24px'}
                  title="svg"
                  style={{
                    color: selectedIndex === index ? '#725CFF' : '#0C0C0C',
                    minWidth: '24px',
                    minHeight: '24px',
                  }}
                  className={styles.image}
                />

                <div className={classNames([styles.subTitle, selectedIndex === index && styles.subTitleActive])}>
                  {item.title}
                </div>
              </div>
              {selectedIndex === index && <p className={styles.subDesc}>{item.desc}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.btnContainer} onClick={onTrackClick}>
        <Button className={styles.trackingBtn}>{t(`Start Tracking Now!`)}</Button>
      </div>
    </div>
  ) : null;
}
